import React, { useMemo, useState, useRef, useEffect } from 'react';
import i18n from '../i18n';
import { Input } from './ui/input';
import { ChevronDown, Check, Search } from 'lucide-react';

/**
 * PhoneInput — selettore prefisso paese + input numero.
 * Output (via onChange callback): `{ dial_code, national, e164 }`.
 * Internally controlled by the dial + national pair; if only `value` (E.164) is supplied
 * for prefill we parse it once.
 */

// Curated list — most relevant markets for HCF + main EU/global ones.
// Order: Italy first (default), then Europe, then HCF markets, then rest.
export const COUNTRY_DIAL_CODES = [
  { code: 'IT', dial: '+39',  name: 'Italia',           flag: '🇮🇹' },
  { code: 'CH', dial: '+41',  name: 'Svizzera',         flag: '🇨🇭' },
  { code: 'FR', dial: '+33',  name: 'Francia',          flag: '🇫🇷' },
  { code: 'DE', dial: '+49',  name: 'Germania',         flag: '🇩🇪' },
  { code: 'ES', dial: '+34',  name: 'Spagna',           flag: '🇪🇸' },
  { code: 'GB', dial: '+44',  name: 'Regno Unito',      flag: '🇬🇧' },
  { code: 'NL', dial: '+31',  name: 'Paesi Bassi',      flag: '🇳🇱' },
  { code: 'BE', dial: '+32',  name: 'Belgio',           flag: '🇧🇪' },
  { code: 'AT', dial: '+43',  name: 'Austria',          flag: '🇦🇹' },
  { code: 'PT', dial: '+351', name: 'Portogallo',       flag: '🇵🇹' },
  { code: 'GR', dial: '+30',  name: 'Grecia',           flag: '🇬🇷' },
  { code: 'IE', dial: '+353', name: 'Irlanda',          flag: '🇮🇪' },
  { code: 'LU', dial: '+352', name: 'Lussemburgo',      flag: '🇱🇺' },
  { code: 'MC', dial: '+377', name: 'Monaco',           flag: '🇲🇨' },
  { code: 'SM', dial: '+378', name: 'San Marino',       flag: '🇸🇲' },
  { code: 'MT', dial: '+356', name: 'Malta',            flag: '🇲🇹' },
  // HCF / Green Paradise markets
  { code: 'AL', dial: '+355', name: 'Albania',          flag: '🇦🇱' },
  { code: 'ID', dial: '+62',  name: 'Indonesia',        flag: '🇮🇩' },
  { code: 'TH', dial: '+66',  name: 'Thailandia',       flag: '🇹🇭' },
  { code: 'AE', dial: '+971', name: 'Emirati Arabi',    flag: '🇦🇪' },
  // Rest of world (common)
  { code: 'US', dial: '+1',   name: 'Stati Uniti',      flag: '🇺🇸' },
  { code: 'CA', dial: '+1',   name: 'Canada',           flag: '🇨🇦' },
  { code: 'AU', dial: '+61',  name: 'Australia',        flag: '🇦🇺' },
  { code: 'BR', dial: '+55',  name: 'Brasile',          flag: '🇧🇷' },
  { code: 'AR', dial: '+54',  name: 'Argentina',        flag: '🇦🇷' },
  { code: 'MX', dial: '+52',  name: 'Messico',          flag: '🇲🇽' },
  { code: 'JP', dial: '+81',  name: 'Giappone',         flag: '🇯🇵' },
  { code: 'KR', dial: '+82',  name: 'Corea del Sud',    flag: '🇰🇷' },
  { code: 'CN', dial: '+86',  name: 'Cina',             flag: '🇨🇳' },
  { code: 'IN', dial: '+91',  name: 'India',            flag: '🇮🇳' },
  { code: 'SG', dial: '+65',  name: 'Singapore',        flag: '🇸🇬' },
  { code: 'HK', dial: '+852', name: 'Hong Kong',        flag: '🇭🇰' },
  { code: 'TR', dial: '+90',  name: 'Turchia',          flag: '🇹🇷' },
  { code: 'IL', dial: '+972', name: 'Israele',          flag: '🇮🇱' },
  { code: 'SA', dial: '+966', name: 'Arabia Saudita',   flag: '🇸🇦' },
  { code: 'EG', dial: '+20',  name: 'Egitto',           flag: '🇪🇬' },
  { code: 'ZA', dial: '+27',  name: 'Sudafrica',        flag: '🇿🇦' },
  { code: 'RU', dial: '+7',   name: 'Russia',           flag: '🇷🇺' },
  { code: 'NO', dial: '+47',  name: 'Norvegia',         flag: '🇳🇴' },
  { code: 'SE', dial: '+46',  name: 'Svezia',           flag: '🇸🇪' },
  { code: 'DK', dial: '+45',  name: 'Danimarca',        flag: '🇩🇰' },
  { code: 'FI', dial: '+358', name: 'Finlandia',        flag: '🇫🇮' },
  { code: 'PL', dial: '+48',  name: 'Polonia',          flag: '🇵🇱' },
  { code: 'CZ', dial: '+420', name: 'Cechia',           flag: '🇨🇿' },
  { code: 'HR', dial: '+385', name: 'Croazia',          flag: '🇭🇷' },
  { code: 'SI', dial: '+386', name: 'Slovenia',         flag: '🇸🇮' },
  { code: 'RO', dial: '+40',  name: 'Romania',          flag: '🇷🇴' },
  { code: 'HU', dial: '+36',  name: 'Ungheria',         flag: '🇭🇺' },
];

function findCountryByDial(dial) {
  if (!dial) return null;
  return COUNTRY_DIAL_CODES.find((c) => c.dial === dial) || null;
}

function parseE164(value) {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  if (!v.startsWith('+')) return null;
  // Try longest dial code first (sorted by length desc)
  const sorted = [...COUNTRY_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (v.startsWith(c.dial)) {
      return { country: c, national: v.substring(c.dial.length).replace(/\s+/g, '') };
    }
  }
  return null;
}

export function PhoneInput({
  value,
  onChange,
  required = true,
  defaultCountry = 'IT',
  placeholder = 'Numero di telefono',
  className = '',
  testIdPrefix = 'phone',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState(() => {
    // Try to prefill from E.164 value
    const parsed = parseE164(value);
    if (parsed) return parsed.country;
    return COUNTRY_DIAL_CODES.find((c) => c.code === defaultCountry) || COUNTRY_DIAL_CODES[0];
  });
  const [national, setNational] = useState(() => {
    const parsed = parseE164(value);
    if (parsed) return parsed.national;
    return '';
  });
  const wrapRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Emit changes upward.
  // ★ 2026-07-29 (hooks fix): `onChange` intentionally omitted — parent may
  // pass a fresh function reference on every render, which would cause an
  // infinite loop. Callers are expected to memoize `onChange` with useCallback
  // if they want extra safety; this effect only reacts to actual value changes.
  useEffect(() => {
    const cleanNational = (national || '').replace(/[^\d]/g, '');
    const e164 = cleanNational ? `${country.dial}${cleanNational}` : '';
    if (typeof onChange === 'function') {
      onChange({ dial_code: country.dial, country_code: country.code, national: cleanNational, e164 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, national]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [search]);

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="h-12 px-3 rounded-md border border-input bg-background text-sm flex items-center gap-1.5 hover:bg-[var(--surface-2)] transition-colors min-w-[110px]"
          aria-expanded={open}
          aria-label={i18n.t('phoneInput.selectPrefix', 'Seleziona prefisso paese')}
          data-testid={`${testIdPrefix}-dial-trigger`}
        >
          <span className="text-lg leading-none">{country.flag}</span>
          <span className="font-medium tracking-wide">{country.dial}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
        </button>
        <Input
          type="tel"
          inputMode="numeric"
          placeholder={placeholder}
          value={national}
          onChange={(e) => setNational(e.target.value)}
          required={required}
          className="h-12 flex-1"
          data-testid={`${testIdPrefix}-national`}
        />
      </div>

      {open && (
        <div
          className="absolute z-50 left-0 mt-1 w-[320px] max-w-[calc(100vw-32px)] rounded-lg bg-popover shadow-xl border border-border overflow-hidden"
          role="listbox"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="px-3 py-2 border-b border-border bg-[var(--surface-2)] flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={i18n.t('phoneInput.searchPh', 'Cerca paese o prefisso...')}
              className="bg-transparent text-sm flex-1 outline-none placeholder:text-muted-foreground"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.map((c) => (
              <li key={`${c.code}-${c.dial}`}>
                <button
                  type="button"
                  onClick={() => {
                    setCountry(c);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`w-full px-3 py-2 flex items-center gap-2 text-sm hover:bg-[var(--surface-2)] transition-colors text-left ${
                    c.code === country.code ? 'bg-[var(--gp-gold)]/10' : ''
                  }`}
                  data-testid={`${testIdPrefix}-option-${c.code}`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  <span className="text-muted-foreground tabular-nums">{c.dial}</span>
                  {c.code === country.code && <Check className="w-3.5 h-3.5 text-[var(--gp-gold)]" />}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted-foreground text-center">{i18n.t('phoneInput.noCountry', 'Nessun paese trovato')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PhoneInput;
